import { useState } from 'react';
import { Button } from '../../ui/components';
import { X, CheckCircle, Eye, EyeOff, ShieldCheck, Loader2 } from 'lucide-react';
import OwenMessage from '../Owen/OwenMessage';

const AccountCreationModal = ({ open, onClose, onAccountCreated, savedData, loanPurpose }) => {
  const [authMethod, setAuthMethod] = useState(null); // null, 'email', or provider name
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    agreeToTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const federatedProviders = [
    { id: 'google', name: 'Google', color: '#DB4437', bgColor: '#FFF', borderColor: '#DB4437' },
    { id: 'facebook', name: 'Facebook', color: '#FFF', bgColor: '#1877F2', borderColor: '#1877F2' },
  ];

  const handleFederatedAuth = (provider) => {
    // Mockup: Simulate federated authentication
    console.log(`Authenticating with ${provider.name}...`);
    setAuthMethod(provider.id);
    
    // Simulate successful authentication after a brief delay
    setTimeout(() => {
      onAccountCreated({
        method: provider.id,
        email: `user@${provider.id}.com`,
        firstName: 'Demo',
        lastName: 'User',
      });
    }, 1000);
  };

  const handleEmailSignup = () => {
    // Validate form
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Mockup: Simulate account creation
    console.log('Creating account with email...', formData);
    setAuthMethod('email');
    
    setTimeout(() => {
      onAccountCreated({
        method: 'email',
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
    }, 1000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Shared input style
  const inputStyle = (hasError) => ({
    width: '100%', padding: '10px 12px', fontSize: 'var(--lk-text-sm)',
    border: `1px solid ${hasError ? 'var(--lk-danger-500)' : 'var(--lk-border)'}`,
    borderRadius: 'var(--lk-radius-md)', outline: 'none',
    backgroundColor: 'var(--lk-bg-surface-1)',
  });

  const labelStyle = { display: 'block', fontSize: 'var(--lk-text-sm)', fontWeight: 500, marginBottom: 4, color: 'var(--lk-text-secondary)' };
  const errorStyle = { fontSize: 'var(--lk-text-xs)', color: 'var(--lk-danger-500)', marginTop: 4 };

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{
        position: 'relative', backgroundColor: 'var(--lk-bg-surface-1)',
        borderRadius: 'var(--lk-radius-xl)', width: '100%', maxWidth: 640,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: 'var(--lk-shadow-xl)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--lk-space-5) var(--lk-space-6)',
          borderBottom: '1px solid var(--lk-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              backgroundColor: 'var(--lk-primary-50)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <ShieldCheck size={18} color="var(--lk-primary-500)" />
            </div>
            <h5 style={{ margin: 0, fontWeight: 600, fontSize: 'var(--lk-text-md)' }}>
              {loanPurpose === 'refinance'
                ? "Unlock Better Rates on Your Current Home"
                : "You're One Step Closer to Approval"}
            </h5>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color="var(--lk-text-tertiary)" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 'var(--lk-space-6)' }}>
          {/* Subtitle */}
          <p style={{ color: 'var(--lk-text-tertiary)', textAlign: 'center', marginBottom: 'var(--lk-space-5)', fontSize: 'var(--lk-text-sm)' }}>
            {loanPurpose === 'refinance'
              ? "Create a free account to see if refinancing could lower your monthly payment"
              : "Create a free account to continue your application and get fully prepared for loan approval"}
          </p>

          {/* Owen's Message */}
          <div style={{ marginBottom: 'var(--lk-space-4)' }}>
            <OwenMessage
              message={
                <div>
                  <p style={{ marginBottom: 8 }}>
                    Excellent choice! Continuing your application is the next step in the LoanKnow process.
                    Here's what happens next:
                  </p>
                  <div>
                    <strong>1.</strong> Complete your full application (takes ~10 minutes)<br/>
                    <strong>2.</strong> Upload documents to verify your information<br/>
                    <strong>3.</strong> Get pre-underwritten for maximum approval confidence<br/>
                    <strong>4.</strong> Receive competitive offers from multiple lenders
                  </div>
                </div>
              }
              type="info"
            />
          </div>

          {/* Benefits */}
          <div style={{
            backgroundColor: 'var(--lk-bg-surface-2)', borderRadius: 'var(--lk-radius-lg)',
            padding: 'var(--lk-space-4)', marginBottom: 'var(--lk-space-4)',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)' }}>
              {[
                { text: <><strong>Get closer to full loan readiness</strong> with each step</> },
                { text: <>Watch your <strong>Readiness Score</strong> increase in real-time</> },
                { text: <>Be <strong>fully prepared</strong> before talking to any lender</> },
                { text: <><strong>Close faster</strong> (7-10 days vs 30-45 days typical)</> },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                  <CheckCircle size={16} color="var(--lk-success-500)" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 'var(--lk-text-xs)' }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {authMethod ? (
            // Loading state
            <div style={{ textAlign: 'center', padding: 'var(--lk-space-8) 0' }}>
              <Loader2 size={32} color="var(--lk-primary-500)" style={{ animation: 'spin 1s linear infinite', marginBottom: 'var(--lk-space-3)' }} />
              <p style={{ color: 'var(--lk-text-tertiary)' }}>Creating your account...</p>
            </div>
          ) : (
            <>
              {/* Federated Auth Providers */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-4)' }}>
                {federatedProviders.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => handleFederatedAuth(provider)}
                    style={{
                      width: '100%', padding: '12px 16px', fontSize: 'var(--lk-text-sm)',
                      fontWeight: 600, borderRadius: 'var(--lk-radius-md)', cursor: 'pointer',
                      border: `1px solid ${provider.borderColor}`,
                      backgroundColor: provider.bgColor,
                      color: provider.id === 'google' ? provider.borderColor : provider.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                  >
                    Continue with {provider.name}
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', margin: 'var(--lk-space-4) 0' }}>
                <div style={{ flex: 1, height: 1, backgroundColor: 'var(--lk-border)' }} />
                <span style={{ padding: '0 var(--lk-space-3)', color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-xs)' }}>or sign up with email</span>
                <div style={{ flex: 1, height: 1, backgroundColor: 'var(--lk-border)' }} />
              </div>

              {/* Email Signup Form */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--lk-space-2)' }}>
                  <div>
                    <label style={labelStyle}>First Name</label>
                    <input type="text" value={formData.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)} style={inputStyle(errors.firstName)} />
                    {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
                  </div>
                  <div>
                    <label style={labelStyle}>Last Name</label>
                    <input type="text" value={formData.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} style={inputStyle(errors.lastName)} />
                    {errors.lastName && <div style={errorStyle}>{errors.lastName}</div>}
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Email Address</label>
                  <input type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} style={inputStyle(errors.email)} />
                  {errors.email && <div style={errorStyle}>{errors.email}</div>}
                </div>

                <div>
                  <label style={labelStyle}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      style={{ ...inputStyle(errors.password), paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                    >
                      {showPassword ? <EyeOff size={16} color="var(--lk-text-tertiary)" /> : <Eye size={16} color="var(--lk-text-tertiary)" />}
                    </button>
                  </div>
                  {errors.password && <div style={errorStyle}>{errors.password}</div>}
                  <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)', marginTop: 4 }}>Must be at least 8 characters</div>
                </div>

                <div>
                  <label style={labelStyle}>Confirm Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      style={{ ...inputStyle(errors.confirmPassword), paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                    >
                      {showConfirmPassword ? <EyeOff size={16} color="var(--lk-text-tertiary)" /> : <Eye size={16} color="var(--lk-text-tertiary)" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <div style={errorStyle}>{errors.confirmPassword}</div>}
                </div>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.agreeToTerms}
                    onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                    style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--lk-primary-500)' }}
                  />
                  <span style={{ fontSize: 'var(--lk-text-xs)', color: errors.agreeToTerms ? 'var(--lk-danger-500)' : 'var(--lk-text-tertiary)' }}>
                    I agree to the <a href="#" style={{ color: 'var(--lk-primary-500)' }}>Terms of Service</a> and <a href="#" style={{ color: 'var(--lk-primary-500)' }}>Privacy Policy</a>
                  </span>
                </label>

                {errors.agreeToTerms && (
                  <div style={{
                    padding: 'var(--lk-space-3)', backgroundColor: 'var(--lk-danger-50)',
                    borderRadius: 'var(--lk-radius-md)', color: 'var(--lk-danger-700)',
                    fontSize: 'var(--lk-text-xs)',
                  }}>
                    {errors.agreeToTerms}
                  </div>
                )}

                <Button variant="primary" size="lg" onClick={handleEmailSignup} style={{ width: '100%' }}>
                  Create Account
                </Button>
              </div>

              {/* Footer */}
              <div style={{ marginTop: 'var(--lk-space-4)', textAlign: 'center' }}>
                <span style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>
                  Already have an account?{' '}
                  <a href="#" style={{ color: 'var(--lk-primary-500)', fontWeight: 600, textDecoration: 'none' }}>Sign In</a>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountCreationModal;
